/*
 This file is part of GNU Taler
 (C) 2022 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */
import { setupI18n } from "@gnu-taler/taler-util";
import {
  ComponentChild,
  ComponentChildren,
  Fragment,
  FunctionalComponent,
  FunctionComponent,
  h,
  render,
  VNode,
} from "preact";
import { useEffect, useErrorBoundary, useRef, useState } from "preact/hooks";
import { ExampleItemSetup } from "./tests/hook.js";

const MOBILE_MEDIA_QUERY = "(max-width: 767px)";

const STORY_BROWSER_STYLES = `
  html, body {
    margin: 0;
    min-height: 100%;
  }

  .taler-stories-page,
  .taler-stories-toolbar *,
  .taler-stories-sidebar *,
  .taler-stories-content {
    box-sizing: border-box;
  }

  .taler-stories-page {
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    display: flex;
    align-items: flex-start;
    color: #172033;
    background: #f7f8fa;
    font-family: Arial, Helvetica, sans-serif;
  }

  .taler-stories-toolbar {
    display: none;
  }

  .taler-stories-sidebar {
    position: sticky;
    top: 0;
    z-index: 20;
    flex: 0 0 260px;
    width: 260px;
    height: 100vh;
    height: 100dvh;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 16px 12px 24px;
    border-right: 1px solid #d9dee8;
    background: #ffffff;
    box-shadow: 2px 0 8px rgba(23, 32, 51, 0.04);
    scroll-behavior: smooth;
  }

  .taler-stories-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 44px;
    margin-bottom: 8px;
  }

  .taler-stories-sidebar-title {
    margin: 0;
    font-size: 18px;
    line-height: 1.25;
  }

  .taler-stories-close-button,
  .taler-stories-menu-button,
  .taler-stories-group-button {
    border: 0;
    font: inherit;
    cursor: pointer;
  }

  .taler-stories-close-button {
    display: none;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    color: #3b455a;
    background: transparent;
    font-size: 24px;
  }

  .taler-stories-close-button:hover,
  .taler-stories-close-button:focus-visible,
  .taler-stories-menu-button:hover,
  .taler-stories-menu-button:focus-visible {
    background: #e9edf4;
    outline: 2px solid transparent;
  }

  .taler-stories-language {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 44px;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
  }

  .taler-stories-language select {
    min-height: 36px;
    max-width: 150px;
    border: 1px solid #b8c0cf;
    border-radius: 6px;
    padding: 4px 8px;
    background: #ffffff;
  }

  .taler-stories-navigation {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .taler-stories-group {
    overflow: hidden;
    border: 1px solid #d9dee8;
    border-radius: 8px;
    background: #ffffff;
  }

  .taler-stories-group-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 44px;
    padding: 8px 10px;
    color: #172033;
    background: #f1f3f7;
    text-align: left;
    font-weight: 700;
  }

  .taler-stories-group-button:hover {
    background: #e3e8f0;
  }

  .taler-stories-group-button:focus-visible {
    background: #e3e8f0;
    outline: none;
    box-shadow: inset 0 0 0 2px #526fbd;
  }

  .taler-stories-group-chevron {
    transition: transform 150ms ease;
  }

  .taler-stories-group-button[aria-expanded="true"] .taler-stories-group-chevron {
    transform: rotate(90deg);
  }

  .taler-stories-components {
    margin: 0;
    padding: 0;
    list-style: none;
    padding: 8px;
  }

  .taler-stories-component-link {
    display: flex;
    align-items: center;
    min-height: 44px;
    margin-bottom: 3px;
    padding: 8px;
    border-radius: 6px;
    color: #263248;
    text-decoration: none;
    overflow-wrap: anywhere;
  }

  .taler-stories-component-link:hover,
  .taler-stories-component-link:focus-visible {
    background: #edf1f7;
    outline: 2px solid #526fbd;
    outline-offset: -2px;
  }

  .taler-stories-component-link[aria-current="page"] {
    color: #ffffff;
    background: #3156a3;
    font-weight: 700;
  }

  .taler-stories-content {
    flex: 1 1 auto;
    min-width: 0;
    padding: clamp(16px, 3vw, 32px);
  }

  .taler-stories-story-header {
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 1px solid #d9dee8;
  }

  .taler-stories-story-title {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3em;
    margin: 0;
    color: #172033;
    font-size: clamp(24px, 3vw, 32px);
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .taler-stories-story-title-separator {
    color: #7a8497;
    font-weight: 400;
  }

  .taler-stories-story-dataset {
    color: #3b455a;
    font-weight: 500;
  }

  .taler-stories-dataset-selector {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    color: #3b455a;
    font-size: 14px;
    font-weight: 600;
  }

  .taler-stories-dataset-control {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    min-width: min(320px, 100%);
    border: 1px solid #aab4c5;
    border-radius: 7px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(23, 32, 51, 0.08);
  }

  .taler-stories-dataset-control:hover {
    border-color: #6d7f9e;
  }

  .taler-stories-dataset-control:focus-within {
    border-color: #3156a3;
    outline: 2px solid rgba(49, 86, 163, 0.25);
    outline-offset: 1px;
  }

  .taler-stories-dataset-selector select {
    grid-column: 1 / -1;
    grid-row: 1;
    z-index: 1;
    width: 100%;
    min-height: 36px;
    min-width: 0;
    appearance: none;
    border: 0;
    border-radius: 7px;
    padding: 6px 72px 6px 10px;
    color: #172033;
    background: transparent;
    cursor: pointer;
    font: inherit;
    font-weight: 500;
    outline: none;
  }

  .taler-stories-dataset-position {
    grid-column: 2;
    grid-row: 1;
    z-index: 2;
    padding-left: 8px;
    border-left: 1px solid #d9dee8;
    color: #667188;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    line-height: 20px;
    pointer-events: none;
  }

  .taler-stories-dataset-chevron {
    grid-column: 3;
    grid-row: 1;
    z-index: 2;
    width: 0;
    height: 0;
    margin: 0 11px 0 8px;
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
    border-top: 6px solid #52617a;
    pointer-events: none;
  }

  .taler-stories-backdrop {
    display: none;
  }

  @media (max-width: 767px) {
    .taler-stories-page {
      display: block;
    }

    .taler-stories-toolbar {
      position: sticky;
      top: 0;
      z-index: 30;
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 56px;
      width: 100%;
      padding: 6px 10px;
      border-bottom: 1px solid #d9dee8;
      background: rgba(255, 255, 255, 0.96);
      box-shadow: 0 2px 8px rgba(23, 32, 51, 0.08);
    }

    .taler-stories-menu-button {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 8px;
      color: #263248;
      background: transparent;
      font-size: 24px;
    }

    .taler-stories-current-story {
      min-width: 0;
      overflow: hidden;
      color: #3b455a;
      font-size: 14px;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .taler-stories-sidebar {
      position: fixed;
      inset: 0 auto 0 0;
      z-index: 50;
      width: min(20rem, 88vw);
      height: 100vh;
      height: 100dvh;
      padding-top: 8px;
      transform: translateX(-105%);
      visibility: hidden;
      box-shadow: 8px 0 24px rgba(23, 32, 51, 0.24);
      transition:
        transform 180ms ease,
        visibility 180ms ease;
    }

    .taler-stories-sidebar.is-open {
      transform: translateX(0);
      visibility: visible;
    }

    .taler-stories-close-button {
      display: inline-flex;
    }

    .taler-stories-backdrop {
      position: fixed;
      inset: 0;
      z-index: 40;
      display: block;
      border: 0;
      background: rgba(15, 23, 42, 0.5);
    }

    .taler-stories-content {
      width: 100%;
      min-width: 0;
      padding: 12px;
    }

    .taler-stories-story-header {
      margin-bottom: 16px;
      padding-bottom: 10px;
    }

    .taler-stories-story-title {
      font-size: 22px;
    }

    .taler-stories-dataset-selector {
      align-items: stretch;
      flex-direction: column;
      gap: 6px;
    }

    .taler-stories-dataset-control {
      width: 100%;
      min-width: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .taler-stories-sidebar,
    .taler-stories-group-chevron {
      transition: none;
    }
  }
`;

const Page: FunctionalComponent = ({ children }): VNode => {
  return <div class="taler-stories-page">{children}</div>;
};

function findByGroupComponentName(
  allExamples: Group[],
  group: string,
  component: string,
  name: string,
): ExampleItem | undefined {
  const gl = allExamples.filter((e) => e.title === group);
  if (gl.length === 0) {
    return undefined;
  }
  const cl = gl[0].list.filter((l) => l.name === component);
  if (cl.length === 0) {
    return undefined;
  }
  const el = cl[0].examples.filter((c) => c.name === name);
  if (el.length === 0) {
    return undefined;
  }
  return el[0];
}

function getContentForExample(
  item: ExampleItem | undefined,
  allExamples: Group[],
): FunctionalComponent {
  if (!item)
    return function SelectExampleMessage() {
      return <div>select example from the list on the left</div>;
    };
  const example = findByGroupComponentName(
    allExamples,
    item.group,
    item.component,
    item.name,
  );
  if (!example) {
    return function ExampleNotFoundMessage() {
      return <div>example not found</div>;
    };
  }
  return () => example.render.component(example.render.props);
}

function getComponentForExample(
  item: ExampleItem | undefined,
  allExamples: Group[],
): ComponentItem | undefined {
  if (!item) {
    return undefined;
  }
  return allExamples
    .find((group) => group.title === item.group)
    ?.list.find((component) => component.name === item.component);
}

function getExampleId(item: ExampleItem): string {
  const encode = encodeURIComponent;
  return `${encode(item.group)}-${encode(item.component)}-${encode(item.name)}`;
}

function ExampleList({
  name,
  list,
  selected,
  onSelectStory,
}: {
  name: string;
  list: {
    name: string;
    examples: ExampleItem[];
  }[];
  selected: ExampleItem | undefined;
  onSelectStory: (i: ExampleItem) => void;
}): VNode {
  const [isOpen, setOpen] = useState(true);
  const groupId = `story-group-${encodeURIComponent(name)}`;

  useEffect(() => {
    if (selected?.group === name) {
      setOpen(true);
    }
  }, [name, selected?.group]);

  return (
    <section class="taler-stories-group">
      <button
        type="button"
        class="taler-stories-group-button"
        aria-expanded={isOpen}
        aria-controls={groupId}
        onClick={() => setOpen(!isOpen)}
      >
        <span>{name}</span>
        <span class="taler-stories-group-chevron" aria-hidden="true">
          ›
        </span>
      </button>
      <ul id={groupId} class="taler-stories-components" hidden={!isOpen}>
        {list.flatMap((component) => {
          const firstDataset = component.examples[0];
          if (!firstDataset) {
            return [];
          }
          const exampleId = getExampleId(firstDataset);
          const isSelected =
            selected?.group === firstDataset.group &&
            selected.component === firstDataset.component;
          return [
            <li id={exampleId} key={component.name}>
              <a
                class="taler-stories-component-link"
                href={`#${exampleId}`}
                aria-current={isSelected ? "page" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  onSelectStory(firstDataset);
                }}
              >
                {component.name}
              </a>
            </li>,
          ];
        })}
      </ul>
    </section>
  );
}

/**
 * Prevents the UI from redirecting and inform the dev
 * where the <a /> should have redirected
 * @returns
 */
function PreventLinkNavigation({
  children,
}: {
  children: ComponentChildren;
}): VNode {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const preventNavigation = (event: MouseEvent): void => {
      if (!(event.target instanceof Element)) {
        return;
      }
      const link = event.target.closest<HTMLAnchorElement>("a[href]");
      const href = link?.getAttribute("href");
      if (
        !link ||
        !href ||
        !container.contains(link) ||
        link.hasAttribute("download")
      ) {
        return;
      }
      alert(`should navigate to: ${href}`);
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
    };
    container.addEventListener("click", preventNavigation);
    return () => container.removeEventListener("click", preventNavigation);
  }, []);

  return <div ref={containerRef}>{children}</div>;
}

function ErrorReport({
  children,
  selected,
}: {
  children: ComponentChild;
  selected: ExampleItem | undefined;
}): VNode {
  const [error, resetError] = useErrorBoundary();
  //if there is an error, reset when unloading this component
  useEffect(() => (error ? resetError : undefined));
  if (error) {
    return (
      <div>
        <p>Error was thrown trying to render</p>
        {selected && (
          <ul>
            <li>
              <b>group</b>: {selected.group}
            </li>
            <li>
              <b>component</b>: {selected.component}
            </li>
            <li>
              <b>example</b>: {selected.name}
            </li>
            <li>
              <b>args</b>:{" "}
              <pre>{JSON.stringify(selected.render.props, undefined, 2)}</pre>
            </li>
          </ul>
        )}
        <p>{error.message}</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }
  return <Fragment>{children}</Fragment>;
}

function getSelectionFromLocationHash(
  hash: string,
  allExamples: Group[],
): ExampleItem | undefined {
  if (!hash) return undefined;
  const parts = hash.substring(1).split("-");
  if (parts.length < 3) return undefined;
  return findByGroupComponentName(
    allExamples,
    decodeURIComponent(parts[0]),
    decodeURIComponent(parts[1]),
    decodeURIComponent(parts[2]),
  );
}

function parseExampleImport(
  group: string,
  componentName: string,
  im: MaybeComponent,
): ComponentItem {
  const examples: ExampleItem[] = Object.entries(im)
    .filter(([k]) => k !== "default")
    .map(([exampleName, exampleValue]): ExampleItem => {
      if (!exampleValue) {
        throw Error(
          `example "${exampleName}" from component "${componentName}" in group "${group}" is undefined`,
        );
      }

      if (typeof exampleValue === "function") {
        return {
          group,
          component: componentName,
          name: exampleName,
          render: {
            component: exampleValue as FunctionComponent,
            props: {},
            contextProps: {},
          },
        };
      }
      const v: any = exampleValue;
      if (
        "component" in v &&
        typeof v.component === "function" &&
        "props" in v
      ) {
        return {
          group,
          component: componentName,
          name: exampleName,
          render: v,
        };
      }
      throw Error(
        `example "${exampleName}" from component "${componentName}" in group "${group}" doesn't follow one of the two ways of example`,
      );
    });
  return {
    name: componentName,
    examples,
  };
}

export function parseGroupImport(
  groups: Record<string, ComponentOrFolder>,
): Group[] {
  return Object.entries(groups).map(([groupName, value]) => {
    return {
      title: groupName,
      list: Object.entries(value).flatMap(([key, value]) =>
        folder(groupName, value),
      ),
    };
  });
}

export interface Group {
  title: string;
  list: ComponentItem[];
}

export interface ComponentItem<Props extends object = {}> {
  name: string;
  examples: ExampleItem<Props>[];
}

export interface ExampleItem<Props extends object = {}> {
  group: string;
  component: string;
  name: string;
  render: ExampleItemSetup<Props>;
}

type ComponentOrFolder = MaybeComponent | MaybeFolder;
interface MaybeFolder {
  default?: { title: string };
  // [exampleName: string]: FunctionalComponent;
}
interface MaybeComponent {
  // default?: undefined;
  [exampleName: string]: undefined | object;
}

function folder(groupName: string, value: ComponentOrFolder): ComponentItem[] {
  let title: string | undefined = undefined;
  try {
    title =
      typeof value === "object" &&
      typeof value.default === "object" &&
      value.default !== undefined &&
      "title" in value.default &&
      typeof value.default.title === "string"
        ? value.default.title
        : undefined;
  } catch {
    throw Error(
      `Could not defined if it is component or folder ${groupName}: ${JSON.stringify(
        value,
        undefined,
        2,
      )}`,
    );
  }
  if (title) {
    const c = parseExampleImport(groupName, title, value as MaybeComponent);
    return [c];
  }
  return Object.entries(value).flatMap(([subkey, value]) =>
    folder(groupName, value),
  );
}

interface Props {
  getWrapperForGroup: (name: string) => FunctionComponent<any>;
  examplesInGroups: Group[];
  langs: Record<string, object>;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const updateMatches = (): void => setMatches(mediaQuery.matches);
    updateMatches();
    mediaQuery.addEventListener("change", updateMatches);
    return () => mediaQuery.removeEventListener("change", updateMatches);
  }, [query]);

  return matches;
}

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter(
    (element) =>
      !element.hidden &&
      !element.closest("[hidden]") &&
      !element.closest('[aria-hidden="true"]'),
  );
}

function Application({
  langs,
  examplesInGroups,
  getWrapperForGroup,
}: Props): VNode {
  const url = new URL(window.location.href);
  const initialSelection = getSelectionFromLocationHash(
    url.hash,
    examplesInGroups,
  );

  const currentLang = url.searchParams.get("lang") || "en";

  if (!langs["en"]) {
    langs["en"] = {};
  }
  setupI18n(currentLang, langs);

  const [selected, updateSelected] = useState<ExampleItem | undefined>(
    initialSelection,
  );
  const [navigationOpen, setNavigationOpen] = useState(false);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const initialHashRef = useRef(url.hash);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (initialHashRef.current) {
      const hash = initialHashRef.current.substring(1);
      const found = document.getElementById(hash);
      if (found) {
        setTimeout(() => {
          found.scrollIntoView({
            block: "center",
          });
        }, 50);
      }
    }
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.inert = isMobile && navigationOpen;
    }
  }, [isMobile, navigationOpen]);

  useEffect(() => {
    if (!isMobile) {
      setNavigationOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !navigationOpen) {
      return;
    }

    const previousFocus = document.activeElement as HTMLElement | null;
    const menuButton = menuButtonRef.current;
    const previousBodyOverflow = document.body.style.overflow;
    const sidebar = sidebarRef.current;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setNavigationOpen(false);
        return;
      }
      if (event.key !== "Tab" || !sidebar) {
        return;
      }
      const focusable = focusableElements(sidebar);
      if (focusable.length === 0) {
        event.preventDefault();
        sidebar.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    queueMicrotask(() => {
      const currentSidebar = sidebarRef.current;
      if (currentSidebar) {
        focusableElements(currentSidebar)[0]?.focus();
      }
    });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      (previousFocus ?? menuButton)?.focus();
    };
  }, [isMobile, navigationOpen]);

  const GroupWrapper = getWrapperForGroup(selected?.group || "default");
  const ExampleContent = getContentForExample(selected, examplesInGroups);
  const selectedComponent = getComponentForExample(selected, examplesInGroups);
  const datasetCount = selectedComponent?.examples.length ?? 0;
  const datasetIndex =
    selectedComponent?.examples.findIndex(
      (dataset) => dataset.name === selected?.name,
    ) ?? -1;
  const selectedLabel = selected
    ? `${selected.component} / ${selected.name}`
    : "Select a story";
  const selectStory = (item: ExampleItem): void => {
    const exampleId = getExampleId(item);
    history.pushState({}, "", `#${exampleId}`);
    updateSelected(item);
    setNavigationOpen(false);
  };

  return (
    <Fragment>
      <style>{STORY_BROWSER_STYLES}</style>
      <Page>
        <header class="taler-stories-toolbar">
          <button
            ref={menuButtonRef}
            type="button"
            class="taler-stories-menu-button"
            aria-label="Open stories navigation"
            aria-controls="taler-stories-sidebar"
            aria-expanded={navigationOpen}
            onClick={() => setNavigationOpen(true)}
          >
            ☰
          </button>
          <span class="taler-stories-current-story" title={selectedLabel}>
            {selectedLabel}
          </span>
        </header>
        {isMobile && navigationOpen && (
          <button
            type="button"
            class="taler-stories-backdrop"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setNavigationOpen(false)}
          />
        )}
        <aside
          ref={sidebarRef}
          id="taler-stories-sidebar"
          class={`taler-stories-sidebar${navigationOpen ? " is-open" : ""}`}
          role={isMobile ? "dialog" : undefined}
          aria-modal={isMobile ? "true" : undefined}
          aria-label="Stories navigation"
          aria-hidden={isMobile && !navigationOpen ? "true" : undefined}
          tabIndex={isMobile ? -1 : undefined}
        >
          <div class="taler-stories-sidebar-header">
            <h1 class="taler-stories-sidebar-title">Stories</h1>
            <button
              type="button"
              class="taler-stories-close-button"
              aria-label="Close stories navigation"
              onClick={() => setNavigationOpen(false)}
            >
              ×
            </button>
          </div>
          <label class="taler-stories-language">
            <span>Language</span>
            <select
              value={currentLang}
              onChange={(e) => {
                const url = new URL(window.location.href);
                url.searchParams.set("lang", e.currentTarget.value);
                window.location.href = url.href;
              }}
            >
              {Object.keys(langs).map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <nav class="taler-stories-navigation" aria-label="Stories">
            {examplesInGroups.map((group) => (
              <ExampleList
                key={group.title}
                name={group.title}
                list={group.list}
                selected={selected}
                onSelectStory={selectStory}
              />
            ))}
          </nav>
        </aside>
        <main
          ref={contentRef}
          class="taler-stories-content"
          aria-hidden={isMobile && navigationOpen ? "true" : undefined}
        >
          {selected && (
            <header class="taler-stories-story-header">
              <h1 class="taler-stories-story-title">
                <span>{selected.component}</span>
                <span
                  class="taler-stories-story-title-separator"
                  aria-hidden="true"
                >
                  ›
                </span>
                <span class="taler-stories-story-dataset">{selected.name}</span>
              </h1>
              <label class="taler-stories-dataset-selector">
                <span>Dataset</span>
                <span class="taler-stories-dataset-control">
                  <select
                    value={selected.name}
                    onChange={(event) => {
                      const dataset = selectedComponent?.examples.find(
                        (example) => example.name === event.currentTarget.value,
                      );
                      if (dataset) {
                        selectStory(dataset);
                      }
                    }}
                  >
                    {selectedComponent?.examples.map((dataset) => (
                      <option key={dataset.name} value={dataset.name}>
                        {dataset.name}
                      </option>
                    ))}
                  </select>
                  <span
                    class="taler-stories-dataset-position"
                    aria-live="polite"
                    aria-label={`Dataset ${datasetIndex + 1} of ${datasetCount}`}
                  >
                    {datasetIndex + 1}/{datasetCount}
                  </span>
                  <span
                    class="taler-stories-dataset-chevron"
                    aria-hidden="true"
                  />
                </span>
              </label>
            </header>
          )}
          <ErrorReport selected={selected}>
            <PreventLinkNavigation>
              <GroupWrapper {...(selected?.render.contextProps ?? {})}>
                <ExampleContent />
              </GroupWrapper>
            </PreventLinkNavigation>
          </ErrorReport>
        </main>
      </Page>
    </Fragment>
  );
}

export interface Options {
  id?: string;
  strings?: any;
  getWrapperForGroup?: (name: string) => FunctionComponent<any>;
}

export function renderStories(
  groups: Record<string, ComponentOrFolder>,
  options: Options = {},
): void {
  const examples = parseGroupImport(groups);

  try {
    const cid = options.id ?? "container";
    const container = document.getElementById(cid);
    if (!container) {
      throw Error(
        `container with id ${cid} not found, can't mount page contents`,
      );
    }
    render(
      <Application
        examplesInGroups={examples}
        getWrapperForGroup={options.getWrapperForGroup ?? (() => Fragment)}
        langs={options.strings ?? { en: {} }}
      />,
      container,
    );
  } catch (e) {
    console.error("got error", e);
    if (e instanceof Error) {
      document.body.innerText = `Fatal error: "${e.message}".  Please report this bug at https://bugs.gnunet.org/.`;
    }
  }
}
